import { LocationType } from '~/client/graph'
import Building from '~/client/src/shared/models/LocationObjects/Building'
import Level from '~/client/src/shared/models/LocationObjects/Level'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'

import { getSortKey } from '../../SitemapHelpers/utils/GLMarkerImageGenerator'

export interface IStackLocationRow {
  itemId: string
  depth: number
}

export interface IStackRowsInput {
  locationIds: string[]
  parentIdOf: (id: string) => string | undefined
  compare: (a: string, b: string) => number
}

function nearestMemberAncestor(
  id: string,
  members: Set<string>,
  parentIdOf: (id: string) => string | undefined,
): string | undefined {
  const visited = new Set([id])
  let cursor = parentIdOf(id)
  while (cursor != null && !visited.has(cursor)) {
    if (members.has(cursor)) return cursor
    visited.add(cursor)
    cursor = parentIdOf(cursor)
  }
  return undefined
}

export function buildStackRows({
  locationIds,
  parentIdOf,
  compare,
}: IStackRowsInput): IStackLocationRow[] {
  const members = new Set(locationIds)
  const roots: string[] = []
  const childrenOf = new Map<string, string[]>()

  for (const id of members) {
    const parentId = nearestMemberAncestor(id, members, parentIdOf)
    if (parentId == null) {
      roots.push(id)
    } else {
      childrenOf.set(parentId, [...(childrenOf.get(parentId) ?? []), id])
    }
  }

  const rows: IStackLocationRow[] = []
  const emitted = new Set<string>()
  const visit = (id: string, depth: number) => {
    if (emitted.has(id)) return
    emitted.add(id)
    rows.push({ itemId: id, depth })
    for (const childId of (childrenOf.get(id) ?? []).sort(compare)) {
      visit(childId, depth + 1)
    }
  }

  roots.sort(compare).forEach(id => visit(id, 0))
  // Members whose member ancestors loop back on themselves have no root to hang from.
  const orphans = [...members].filter(id => !emitted.has(id)).sort(compare)
  orphans.forEach(id => visit(id, 0))

  return rows
}

/**
 * Siblings in the stack list: a building's levels as its elevator panel orders them, everything
 * else by type rank, then name. `getLocation` must resolve any location id — a level's building
 * need not be in the stack.
 */
export function stackSiblingOrder(
  locationIds: string[],
  getLocation: (id: string) => LocationBase | undefined,
): (a: string, b: string) => number {
  const levelsByBuilding = new Map<Building, Level[]>()
  for (const id of locationIds) {
    const level = getLocation(id)
    if (!level?.is(LocationType.Level) || !level.parent) continue
    const building = getLocation(level.parent.parentId)
    if (!building?.is(LocationType.Building)) continue
    const key = building as Building
    levelsByBuilding.set(key, [
      ...(levelsByBuilding.get(key) ?? []),
      level as Level,
    ])
  }

  const levelRank = new Map<string, number>()
  for (const [building, levels] of levelsByBuilding) {
    building
      .sortLevelsByCustomOrder(levels)
      .forEach((level, index) => levelRank.set(level.id, index))
  }

  return (a, b) => {
    const locationA = getLocation(a)
    const locationB = getLocation(b)
    if (
      levelRank.has(a) &&
      levelRank.has(b) &&
      locationA.parent.parentId === locationB.parent.parentId
    ) {
      return levelRank.get(a) - levelRank.get(b)
    }
    return (
      getSortKey(locationA?.iconName) - getSortKey(locationB?.iconName) ||
      (locationA?.name ?? '').localeCompare(locationB?.name ?? '', undefined, {
        numeric: true,
      })
    )
  }
}
